import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomCart } from '@cart/const';
import { ActiveCartService, CartVoucherService } from '@cart/data-access';
import { SdvorIconComponent } from '@sdvor/shared/ui/v2';
import { IconTypes } from '@shared/v2';
import { map } from 'rxjs';
import { CartTotalsCouponLoaderComponent } from './cart-totals-coupon-loader';

@Component({
  selector: 'sd-cart-totals-coupon',
  imports: [ReactiveFormsModule, SdvorIconComponent, CartTotalsCouponLoaderComponent],
  templateUrl: './cart-totals-coupon.component.html',
  styleUrls: ['./cart-totals-coupon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartTotalsCouponComponent {
  private readonly cartVoucherService = inject(CartVoucherService);
  private readonly activeCartService = inject(ActiveCartService);

  private readonly cart = toSignal(this.activeCartService.getActive(), { initialValue: null });

  protected readonly iconTypes = IconTypes;
  protected readonly isError = signal(false);

  protected appliedCertificates = computed(() => (this.cart() as CustomCart).appliedCertificates?.[0] ?? null);
  protected appliedVouchers = computed(() => (this.cart() as CustomCart).appliedVouchers?.[0] ?? null);

  protected readonly errorMessage = computed(() => {
    const error = this.errorVoucher();

    if (this.isError() && error) {
      return this.buildCouponErrorMessage(error);
    }

    return '';
  });

  protected readonly form = new FormGroup({ coupon: new FormControl('', [Validators.required]) });

  protected readonly hasValue = toSignal(this.form.controls.coupon.valueChanges.pipe(map((v) => !!v?.trim())), {
    initialValue: false,
  });

  protected readonly isLoadingCoupon = toSignal(this.cartVoucherService.getAddVoucherResultLoading(), {
    initialValue: false,
  });

  protected readonly errorVoucher = toSignal(this.cartVoucherService.getAddVoucherError(), { initialValue: null });

  constructor() {
    this.cartVoucherService.resetAddVoucherProcessingState();

    this.cartVoucherService
      .getAddVoucherResultSuccess()
      .pipe(takeUntilDestroyed())
      .subscribe((success) => {
        if (success) {
          this.isError.set(false);
          this.form.reset();
          this.cartVoucherService.resetAddVoucherProcessingState();
        }
      });

    this.cartVoucherService
      .getAddVoucherResultError()
      .pipe(takeUntilDestroyed())
      .subscribe((error) => {
        if (error) {
          this.isError.set(true);
          this.cartVoucherService.resetAddVoucherProcessingState();
        }
      });

    this.form.controls.coupon.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.isError()) {
        this.isError.set(false);
      }
    });
  }

  protected applyVoucher(): void {
    const couponCode = this.form.value.coupon?.trim();

    if (!couponCode) {
      this.form.controls.coupon.setErrors({ whitespace: true });
      this.form.markAllAsTouched();

      return;
    }

    this.cartVoucherService.addVoucher(couponCode);
  }

  protected removeVoucher(voucherId?: string): void {
    if (this.isError()) {
      this.reset();

      return;
    }

    if (voucherId) {
      this.cartVoucherService.removeVoucher(voucherId);
    }
  }

  private buildCouponErrorMessage(error: any): string {
    if (error.status === 404) {
      return 'по данному коду ничего не найдено';
    }

    switch (error.details?.[0]?.message) {
      case 'coupon.not.found.for.site':
        return 'по данному коду ничего не найдено';
      case 'coupon.not.active.expired':
        return 'промокод не активен';
      case 'coupon.already.redeemed':
        return 'промокод уже использован';
      case 'certificate.limit.exceeded':
        return 'баланс сертификата закончился';
      case 'coupon.already.exists.cart':
        return 'промокод/сертификат уже применён';
      case 'coupon.not.found':
        return 'промокод не найден';
      default:
        return 'ошибка применения промокода';
    }
  }

  private reset(): void {
    this.form.reset();
    this.isError.set(false);
  }
}
